import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { makeRequest2 } from "../../../../../../utils/makeRequest";
import { toastify } from "../../../../../../utils/toast";
import { useFacilityStore } from "../../../../../../app/z-store/store";
import { acknowledgeWaterSensorAlertURL, getWaterSensorAlertsURL } from "../../../../../../utils/urls";
import { ALERT_BADGES, ALERT_LABELS, formatDateTime, timeAgo } from "../utils/format";
import { viewTankPath } from "../utils/routes";

const REFRESH_MS = 60000;

function AlertsTab() {
  const navigate = useNavigate();
  const facilityId = useFacilityStore((state) => state.facilityId);
  const [view, setView] = useState("active");
  const [alerts, setAlerts] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [loading, setLoading] = useState(true);

  const getAlerts = async (selectedView = view, offset = first, limit = rows) => {
    if (!facilityId) return;
    setLoading(true);
    const page = Math.floor(offset / limit) + 1;
    const activeFilter = selectedView === "active" ? "&active=true" : "";
    const response = await makeRequest2(`${getWaterSensorAlertsURL(facilityId)}?page=${page}&limit=${limit}${activeFilter}`, "GET");
    if (response.success) {
      setAlerts(response.data.alerts || []);
      setTotalRecords(response.data.pagination?.totalCount || 0);
    } else {
      toastify(response.error, "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    getAlerts(view, first, rows);
    const refresh = setInterval(() => getAlerts(view, first, rows), REFRESH_MS);
    return () => clearInterval(refresh);
  }, [view, first, rows, facilityId]);

  const handleAcknowledge = async (alert) => {
    const response = await makeRequest2(acknowledgeWaterSensorAlertURL(facilityId, alert._id), "PATCH", {});
    if (response.success) {
      toastify("Alert acknowledged", "success");
      getAlerts();
    } else {
      toastify(response.error, "error");
    }
  };

  const typeTemplate = (alert) => {
    const tone = alert.active ? ALERT_BADGES[alert.type] : "secondary";
    return (
      <span className={`badge bg-light-${tone} text-${tone}`}>
        <i className={`ti ${alert.active ? "ti-alert-triangle" : "ti-check"} me-1`}></i>
        {ALERT_LABELS[alert.type]}
      </span>
    );
  };

  const tankTemplate = (alert) =>
    alert.tankId ? (
      <button className="btn btn-link p-0" onClick={() => navigate(viewTankPath(alert.tankId._id))}>
        {alert.tankId.name}
      </button>
    ) : (
      <span className="text-muted">Deleted tank</span>
    );

  const statusTemplate = (alert) =>
    alert.active ? (
      <span className="text-danger fw-semibold">Active</span>
    ) : (
      <span className="text-muted">Resolved {timeAgo(alert.resolvedAt)}</span>
    );

  const actionTemplate = (alert) =>
    alert.acknowledged ? (
      <span className="text-muted small" title={formatDateTime(alert.acknowledgedAt)}>
        <i className="ti ti-check me-1"></i>Acknowledged
      </span>
    ) : alert.active ? (
      <button className="btn btn-sm btn-outline-primary" onClick={() => handleAcknowledge(alert)}>
        Acknowledge
      </button>
    ) : null;

  return (
    <div className="card">
      <div className="card-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <h5 className="mb-0">Alerts</h5>
        <div className="btn-group btn-group-sm" role="group" aria-label="Alert view">
          {[
            ["active", "Active alerts"],
            ["all", "History"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`btn ${view === key ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => {
                setFirst(0);
                setView(key);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="card-body">
        <DataTable
          value={alerts}
          lazy
          paginator
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          onPage={(e) => {
            setFirst(e.first);
            setRows(e.rows);
          }}
          rowsPerPageOptions={[20, 50, 100]}
          loading={loading}
          emptyMessage={view === "active" ? "All clear — no active alerts." : "No alerts yet."}
          stripedRows
        >
          <Column header="Alert" body={typeTemplate} />
          <Column header="Tank" body={tankTemplate} />
          <Column field="message" header="Details" />
          <Column header="Raised" body={(a) => <span title={formatDateTime(a.createdAt)}>{timeAgo(a.createdAt)}</span>} />
          <Column header="Status" body={statusTemplate} />
          <Column header="" body={actionTemplate} />
        </DataTable>
      </div>
    </div>
  );
}

export default AlertsTab;
